import { Facebook, Copy, Check } from "lucide-react";
import { useState } from "react";

interface FacebookShareButtonProps {
  url: string;
  caption: string;
  variant?: "icon" | "full";
  className?: string;
  /** Optional item id used to attribute clicks in analytics. */
  itemId?: string;
  itemSlug?: string;
  kind?: "news" | "story";
  /** UTM campaign label; defaults to "facebook_share". */
  campaign?: string;
}

function withUtm(
  rawUrl: string,
  params: { source: string; medium: string; campaign: string; content?: string },
) {
  try {
    const u = new URL(rawUrl);
    u.searchParams.set("utm_source", params.source);
    u.searchParams.set("utm_medium", params.medium);
    u.searchParams.set("utm_campaign", params.campaign);
    if (params.content) u.searchParams.set("utm_content", params.content);
    return u.toString();
  } catch {
    return rawUrl;
  }
}

/**
 * Canonicalise a share URL — strip query/hash so Facebook always scrapes
 * the same canonical page (matches <link rel="canonical"> and og:url).
 * Without this, tracking params in the share URL become distinct OG
 * objects in Facebook's cache and every share re-scrapes from scratch.
 */
function toCanonical(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    u.search = "";
    u.hash = "";
    return u.toString();
  } catch {
    return rawUrl;
  }
}

/**
 * Ask our backend to re-scrape Facebook's Open Graph cache for this URL
 * (via the Graph API) so the preview reflects the latest og:image /
 * og:title / og:description without users opening the Sharing Debugger.
 */
function requestOgRefresh(canonicalUrl: string) {
  if (typeof fetch === "undefined") return;
  try {
    const payload = JSON.stringify({ url: canonicalUrl });
    const endpoint = "/api/public/og-refresh";
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(endpoint, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* ignore */
  }
}

/**
 * One-click "Share to Facebook" button. Appends UTM tags so the analytics
 * dashboard can attribute clicks back to Facebook shares, and copies a
 * pre-built caption (Facebook deprecated the `quote` param).
 */
export function FacebookShareButton({
  url,
  caption,
  variant = "full",
  className = "",
  itemId,
  itemSlug,
  kind = "news",
  campaign = "facebook_share",
}: FacebookShareButtonProps) {
  const [copied, setCopied] = useState(false);

  // Facebook honours og:url from the scraped page, so send the canonical
  // URL to sharer.php (matches <link rel="canonical">) — keeps FB's cache
  // key stable across shares instead of forking per UTM combination.
  const canonicalUrl = toCanonical(url);
  const taggedUrl = withUtm(canonicalUrl, {
    source: "facebook",
    medium: "social",
    campaign,
    content: itemSlug || itemId,
  });
  const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`;

  function beacon() {
    // Ask the server to refresh Facebook's OG cache for this canonical URL.
    requestOgRefresh(canonicalUrl);
    if (typeof navigator === "undefined") return;
    try {
      const payload = JSON.stringify({
        kind,
        item_id: itemId ?? null,
        item_slug: itemSlug ?? null,
        canonical_url: canonicalUrl,
        news_slug: kind === "news" ? itemSlug ?? null : null,
        utm_source: "facebook",
        utm_medium: "social",
        utm_campaign: campaign,
        referrer: typeof document !== "undefined" ? document.referrer : null,
      });
      const url = "/api/public/track/social-click";
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          url,
          new Blob([payload], { type: "application/json" }),
        );
      } else {
        fetch(url, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      /* ignore */
    }
  }

  async function copyCaption(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`${caption}\n\n${taggedUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }

  if (variant === "icon") {
    return (
      <div className={`inline-flex items-center gap-1 ${className}`}>
        <a
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Facebook"
          title="Share on Facebook"
          onClick={beacon}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#1877F2] text-white transition hover:opacity-90"
        >
          <Facebook className="h-3.5 w-3.5" />
        </a>
        <button
          type="button"
          onClick={copyCaption}
          aria-label="Copy share caption"
          title={copied ? "Copied!" : "Copy caption"}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border bg-background text-foreground/80 transition hover:border-navy hover:text-navy"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <a
        href={shareUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={beacon}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#1877F2] px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
      >
        <Facebook className="h-3.5 w-3.5" /> Share on Facebook
      </a>
      <button
        type="button"
        onClick={copyCaption}
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground/80 transition hover:border-navy hover:text-navy"
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5 text-emerald-600" /> Caption copied
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5" /> Copy caption
          </>
        )}
      </button>
    </div>
  );
}
